/**
 * Table de paramètres de paie DATÉS (CST §2.2 : aucun taux en dur, historique conservé).
 *
 * Chaque version porte une date d'effet ; le moteur lit la version applicable à la
 * période du bulletin (`parametresPaieAu`). Un nouveau barème, un nouveau SMIG ou un
 * nouveau seuil = une NOUVELLE ligne en tête de table, jamais une modification d'une
 * ligne existante : les bulletins déjà calculés (statut validé/payé) ne sont pas
 * recalculés et les périodes passées restent reproductibles.
 *
 * Sources et statuts : voir `docs/paie/parametres-paie.md`.
 */

export interface TrancheIRPP {
  /** Borne basse de Q (revenu annuel imposable PAR PART, FCFA). */
  min: number;
  /** Borne haute (incluse), `null` = sans borne. */
  max: number | null;
  taux: number;
  /** Déduction N : impôt par part = max(0, taux × Q − N). */
  deduction: number;
}

export interface AbattementFraisProParams {
  /** Activé = l'abattement art. 94 réduit la base IRPP (activé : réponses légales du 16/10/2026). */
  actif: boolean;
  /** CGI art. 94 : 20 %. */
  taux: number;
  /** CGI art. 94 : plafond annuel 10 000 000 FCFA. */
  plafondAnnuel: number;
}

/** Contribution dont la décision est en attente : valeur conservée, interrupteur « actif = non ». */
export interface ContributionEnAttente {
  /** false = non appliquée au bulletin ni au coût employeur (référentiel v2). */
  actif: boolean;
  /** Taux appliqué si `actif` est vrai. */
  taux: number;
}

export interface ContributionsEnAttente {
  /** CSS : retenue salariale, au-dessus de `seuil` FCFA/mois (absente du référentiel v2). */
  css: ContributionEnAttente & { seuil: number };
  /** FGTLS : patronale, sur le brut sans plafond (Loi 3/94 ; absente du référentiel v2). */
  fgtls: ContributionEnAttente;
  /** FNH : patronale, base CNSS plafonnée (taux « à confirmer » au référentiel v2). */
  fnh: ContributionEnAttente;
  /** CFP : patronale, base CNSS plafonnée (taux « à confirmer » au référentiel v2). */
  cfp: ContributionEnAttente;
}

export interface ParametresPaieDates {
  /** Date d'effet (AAAA-MM-JJ), incluse. */
  date_effet: string;
  /** SMIG mensuel (décision dirigeant 16/10/2026). Sert au contrôle du salaire de base. */
  smig: number;
  /** Revenu mensuel minimum (intègre SMIG + primes obligatoires). Paramètre distinct du SMIG. */
  rmm: number;
  /** TCS : part mensuelle exonérée (CGI art. 347). Paramètre distinct du SMIG et du RMM. */
  tcsSeuil: number;
  /** IRPP : barème légal CGI art. 173-174, 8 tranches. */
  baremeIrpp: readonly TrancheIRPP[];
  abattementFraisPro: AbattementFraisProParams;
  /**
   * Exonération art. 91 11° et art. 347 al. 2 (primes de performance, rendement, intéressement, gratifications,
   * bonus, 13e mois, primes de fin d'année) de la base IRPP ET de la base TCS, dans la limite ANNUELLE et GLOBALE de
   * 4 000 000 FCFA (cumul sur l'année civile, voir `CalculPaieRubriques` et `ExonerationArt9111`).
   */
  exonerationArt9111Active: boolean;
  /**
   * CSS, FGTLS, FNH, CFP : DÉSACTIVÉES par défaut (le référentiel v2 ne les contient pas ou
   * indique un taux « à confirmer »). À valider par l'expert-comptable ; ré-activables en
   * passant `actif` à true dans une nouvelle ligne datée (ou par patch entreprise).
   */
  contributionsEnAttente: ContributionsEnAttente;
}

/** Barème légal CGI art. 173-174 (K = t × Q − N, continuité vérifiée par test). */
export const BAREME_IRPP_LEGAL: readonly TrancheIRPP[] = [
  { min: 0, max: 1_500_000, taux: 0, deduction: 0 },
  { min: 1_500_000, max: 1_920_000, taux: 0.05, deduction: 75_000 },
  { min: 1_920_000, max: 2_700_000, taux: 0.1, deduction: 171_000 },
  { min: 2_700_000, max: 3_600_000, taux: 0.15, deduction: 306_000 },
  { min: 3_600_000, max: 5_160_000, taux: 0.2, deduction: 486_000 },
  { min: 5_160_000, max: 7_500_000, taux: 0.25, deduction: 744_000 },
  { min: 7_500_000, max: 11_000_000, taux: 0.3, deduction: 1_119_000 },
  { min: 11_000_000, max: null, taux: 0.35, deduction: 1_669_000 },
];

/**
 * Historique, du plus récent au plus ancien. Ne jamais éditer une ligne publiée :
 * ajouter une ligne plus récente.
 */
export const HISTORIQUE_PARAMETRES_PAIE: readonly ParametresPaieDates[] = [
  {
    // Décisions du dirigeant des 22 et 23/10/2026 (D5 de la recette 1.1.6) : « suivre le CGI ; ce qui ne concerne pas la
    // paie doit être désactivé », puis date d'effet au 1er JANVIER 2026 (la loi rend FNH et CFP dues pour toute l'année 2026).
    //  - FNH 2 % : CGI 2025 art. 401 à 404 (employeurs affiliés à la CNSS ; base = salaires dans la limite du plafond CNSS) : ACTIVÉ.
    //  - CFP 0,50 % : CGI 2025, loi de finances 2017 art. 5 à 12 (sociétés soumises à l'IS, BIC et BNC ; base = rémunération
    //    brute avant retenues, dans la limite du plafond CNSS) : ACTIVÉE.
    //  - CSS : taxe de 1 % sur le chiffre d'affaires (TVA), pas une cotisation de paie : DÉSACTIVÉE.
    //  - FGTLS : aucun texte du CGI : DÉSACTIVÉ.
    // FNH et CFP sont ACTIVABLES PAR ENTREPRISE (statut fiscal et interrupteurs, voir `ContributionsEmployeur.ts`) ; cette
    // ligne fixe la date d'effet et le taux. Les bulletins déjà générés ne sont jamais recalculés.
    date_effet: '2026-01-01',
    smig: 80_000,
    rmm: 150_000,
    tcsSeuil: 150_000,
    baremeIrpp: BAREME_IRPP_LEGAL,
    // CGI art. 94 (Loi 5/2023 art. 94 al. 1 nouveau) : 20 %, plafond 10 000 000 FCFA/an, aucun plancher.
    abattementFraisPro: { actif: true, taux: 0.2, plafondAnnuel: 10_000_000 },
    // CGI 2025 art. 91 11° et 347 : texte lu et établi ; cumul annuel implémenté le 18/10/2026 (règle de la source récente).
    exonerationArt9111Active: true,
    contributionsEnAttente: {
      css: { actif: false, taux: 0.01, seuil: 480_000 },
      fgtls: { actif: false, taux: 0.01 },
      // Taux du texte le plus récent (règle du dirigeant du 18/10/2026) :
      // FNH 2 % (CGI 2025 art. 403, consolidé après l'ordonnance 15/2024 qui disait 3 %),
      // CFP 0,50 % (CGI 2025, loi de finances 2017 art. 9 ; l'ancienne valeur 1,5 % n'avait aucune source).
      fnh: { actif: true, taux: 0.02 },
      cfp: { actif: true, taux: 0.005 },
    },
  },
];

/** 'AAAA-MM' (période de bulletin) devient le 1er du mois ; 'AAAA-MM-JJ' est inchangé. */
function normaliserDate(dateOuPeriode: string): string {
  return /^\d{4}-\d{2}$/.test(dateOuPeriode) ? `${dateOuPeriode}-01` : dateOuPeriode.slice(0, 10);
}

/** Version applicable à une date (en deçà de la première date d'effet : la plus ancienne version). Version des paramètres applicable à une date ('AAAA-MM-JJ') ou une période ('AAAA-MM'). */
export function parametresPaieAu(
  dateOuPeriode?: string,
  historique: readonly ParametresPaieDates[] = HISTORIQUE_PARAMETRES_PAIE,
): ParametresPaieDates {
  const premiere = historique[0]!;
  if (!dateOuPeriode) return premiere;
  const d = normaliserDate(dateOuPeriode);
  for (const v of historique) {
    if (v.date_effet <= d) return v;
  }
  return historique[historique.length - 1]!;
}

/** Version la plus récente (valeurs « courantes » exposées par `Taux.ts`). */
export const PARAMETRES_PAIE_COURANTS: ParametresPaieDates = HISTORIQUE_PARAMETRES_PAIE[0]!;
